<template>
  <div class="container mt-5">
    <div class="page-content">
      <div class="row">
        <div class="col">
          <h1 class="text-center pb-4">{{ $t('bracket.title') }}</h1>
          <p class="text-center" v-if="rounds.length < 1">
            {{ $t('bracket.registrationMessage') }}
          </p>
        </div>
      </div>
      <div v-if="rounds.length > 0" class="row">
        <div class="col d-flex flex-column flex-md-row">
          <a
            class="round-tab"
            :class="{
              active: selectedRound == round,
              'round-generated': round.groupCount > 0
            }"
            v-for="(round, key) in rounds"
            :key="round.orderNum"
            href="#"
            @click="switchRound(round.orderNum)"
          >
            {{ round.name }}
          </a>
        </div>
      </div>
      <div v-if="rounds.length > 0" class="row round-content">
        <div class="col-md-6 col-xl-4 col-xxl-3 pt-4">
          <div v-if="roundGroups[selectedRound.orderNum] && roundGroups[selectedRound.orderNum].length > 1" class="round-group">
            <h5 class="h-round-total">{{ $t('bracket.selectGroup') }}</h5>
        
            <b-form-select v-model="selectedGroup" :options="roundGroups[selectedRound.orderNum]" class="form-select"></b-form-select>
          </div>

          <div class="round-total">
            <h5 class="text-center h-round-total">
              {{ $t('bracket.roundTotal', { round: selectedRound.orderNum }) }}
            </h5>
            <div class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th style="width: 5%">{{ $t('bracket.tableHeaders.position') }}</th>
                    <th>{{ $t('bracket.tableHeaders.team') }}</th>
                    <th style="width: 20%">{{ $t('bracket.tableHeaders.points') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(entry, key) in roundTotals"
                    :key="entry.id"
                    :class="{
                      elim:
                        roundTotals.length > Math.floor(selectedRound.winnerCount / selectedRound.groupCount) &&
                        key >= Math.floor(selectedRound.winnerCount / selectedRound.groupCount),
                    }"
                  >
                    <td>{{ key + 1 }}.</td>
                    <td style="max-width: 170px;">
                      <a href="#" @click="showTeamModal(entry.id)">
                        {{ entry.name }}
                      </a>
                    </td>
                    <td>{{ entry.points }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="status" v-if="!isLastRound">
              {{ $t('bracket.advancementInfo', { 
                count: Math.floor(selectedRound.winnerCount / selectedRound.groupCount),
                groups: roundGroups[selectedRound.orderNum] && roundGroups[selectedRound.orderNum].length > 1 ? ' ' + $t('bracket.eachGroup') : ''
              }) }}
            </p>
          </div>
        </div>
        <div class="col pt-4">
          <div>
            <div class="row flex-row">
              <div
                class="col game"
                v-for="(match, key) in matches"
                :key="match.id"
              >
                <div class="match">
                  <div :class="{
                      'match-h m-cancelled': match.status == 4,
                      'match-h m-finished': match.status == 3,
                      'match-h m-active': match.status == 2,
                      'match-h m-planned': match.status < 2,
                    }">
                    <h4>{{ $t('bracket.gameInfo.gameNumber', { number: match.countId }) }}</h4>
                    <p v-if="match.status == 0">{{ $t('bracket.gameInfo.startsAt', { time: match.startedAt }) }}</p>
                    <p v-else>{{ $t('bracket.gameInfo.startedAt', { time: match.startedAt }) }}</p>
                  </div>
                  <div class="match-d">
                    <div class="table-responsive">
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <th>{{ $t('bracket.tableHeaders.position') }}</th>
                            <th>{{ $t('bracket.tableHeaders.team') }}</th>
                            <th>{{ $t('bracket.tableHeaders.points') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(entry, key) in match.teams"
                            :key="entry.id"
                          >
                            <td>{{ key + 1 }}.</td>
                            <td style="max-width: 170px;">
                              <a href="#" @click="showTeamModal(entry.id)">
                                {{ entry.name }}
                              </a>
                            </td>
                            <td>{{ entry.points }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p v-if="match.status < 2" class="status">{{ $t('bracket.gameStatus.planned') }}</p>
                    <p v-else-if="match.status == 2" class="status">
                      {{ $t('bracket.gameStatus.active') }}
                    </p>
                    <p v-else-if="match.status == 4" class="status">
                      {{ $t('bracket.gameStatus.cancelled') }}
                    </p>
                    <p v-else class="status">{{ $t('bracket.gameStatus.finished') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal ref="team-modal" centered hide-footer>
      <template #modal-header="{ close }">
        <h4 class="modal-title">{{ $t('bracket.teamModal.title', { name: teamData.name }) }}</h4>
        <button
          type="button"
          class="btn-close"
          @click="close()"
          :aria-label="$t('bracket.teamModal.close')"
        ></button>
      </template>
      <p class="mb-0"><strong>{{ $t('bracket.teamModal.members') }}</strong></p>
      <p
        v-html="
          teamData.members
            .join(',<br />')
            .replace(
              teamData.leader,
              teamData.leader + ' (' + $t('bracket.teamModal.leader') + ')'
            )
        "
      ></p>
    </b-modal>
  </div>
</template>

<script>
import { getData } from "../services/BracketService";
import { getOneTeam } from "../services/TeamService";
import { checkLast } from "../services/CacheService";

export default {
  name: "Bracket",
  data() {
    return {
      refreshTimer: null,
      refreshLast: -1,
      bracketData: null,
      rounds: [
        {
          orderNum: 0,
          name: "",
          isActive: false,
          totalTeams: 0,
          winnerCount: 0,
          groupMatches: {}
        },
      ],
      roundGroups: [],
      roundTotals: [],
      matches: [],
      selectedRound: 1,
      selectedGroup: 1,
      defaultRound: 0,
      teamData: {
        name: "",
        members: [
          {
            game_nick: "",
          },
        ],
        teamLeader: {
          game_nick: "",
        },
      },
    };
  },
  methods: {
    updateBracket() {
      getData().then((response) => {
        const previousRoundOrder = this.selectedRound?.orderNum;
        const previousGroup = this.selectedGroup;

        this.bracketData = response;

        this.rounds = this.bracketData.rounds;

        this.roundGroups = [];
        for (const round of this.rounds) {
          const groupCount = Object.keys(round.groupMatches).length;
          round.groupCount = groupCount;

          this.roundGroups[round.orderNum] = [];
          for (var i = 1; i <= groupCount; i++) {
            this.roundGroups[round.orderNum].push(i);
          }
        }

        // Keep current browsing context if it's still valid after refresh.
        let nextRound = this.rounds.find(
          (round) => round.orderNum === previousRoundOrder
        );
        if (!nextRound) {
          nextRound = this.rounds.find((round) => round.isActive) || this.rounds[0];
        }

        const availableGroups = this.roundGroups[nextRound?.orderNum] || [];
        let nextGroup = 1;
        if (availableGroups.length > 0 && availableGroups.includes(previousGroup)) {
          nextGroup = previousGroup;
        }

        this.selectedRound = nextRound;
        this.selectedGroup = nextGroup;
        this.getTotalPoints(this.selectedRound, this.selectedGroup);
        this.getMatches(this.selectedRound, this.selectedGroup);

        this.defaultRound = this.selectedRound;
      });
    },
    getMatches(round, group) {
      const raw = round.groupMatches[group];
      if (!raw) {
        this.matches = [];
        return;
      }
      this.matches = raw.map((match) => ({
        ...match,
        teams: [...match.teams].sort(
          (a, b) => (b.points || 0) - (a.points || 0)
        ),
      }));
    },
    getTotalPoints(round, group) {
      let newTotals = {};
      const roundMatches = round?.groupMatches?.[group];
      if (!roundMatches) {
        this.roundTotals = [];
        return;
      }

      for (const match of roundMatches) {
        for (const team of match.teams) {
          if (team.points === 0) {
            continue;
          }

          if (newTotals[team.id]) {
            newTotals[team.id].points += team.points;
          } else {
            newTotals[team.id] = {
              id: team.id,
              name: team.name,
              points: team.points
            }
          }
        }
      }

      this.roundTotals = Object.values(newTotals).sort((a, b) => b.points - a.points);
    },
    switchRound(num) {
      if (isNaN(num) || this.selectedRound.orderNum === num) {
        return;
      }

      for (const round of this.rounds) {
        if (round.groupCount > 0 && round.orderNum === num) {
          this.selectedRound = round;
          this.selectedGroup = 1;

          this.getTotalPoints(this.selectedRound, this.selectedGroup);
          this.getMatches(this.selectedRound, this.selectedGroup);

          return;
        }
      }
    },
    getOneTeam(team_id) {
      getOneTeam(team_id).then((response) => {
        if (response != null) {
          this.teamData = response;
          this.$refs["team-modal"].show();
        }
      });
    },
    showTeamModal(team_id) {
      this.getOneTeam(team_id);
    },
    pollData() {
      this.polling = setInterval(() => {
        checkLast().then((response) => {
          if (response.last == undefined) {
            return;
          }
          if (this.refreshLast == -1) {
            this.refreshLast = response.last;
          } else if (this.refreshLast != response.last) {
            this.refreshLast = response.last;

            // todo: don't switch to a different round if the player is already browsing
            this.updateBracket();
          }
        });
      }, 15000);
    },
  },
  computed: {
    isLastRound() {
      // Hide message if this is the last round and there are at least 2 rounds
      return this.rounds.length >= 2 && this.selectedRound.orderNum === Math.max(...this.rounds.map(r => r.orderNum));
    }
  },
  watch: {
    selectedGroup(newSelected, oldSelected) {
      this.getTotalPoints(this.selectedRound, newSelected);
      this.getMatches(this.selectedRound, newSelected);
    }
  },
  mounted() {
    this.updateBracket();
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  created() {
    this.pollData();
  },

};
</script>
